import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import React from 'react'
import Drawer from '../../../components/Drawer'
import GrantContent from '../../../components/GrantContent'
import * as Permisos from '../../../utils/Permisos'
import { RamoMark } from '../common/quote-template-common'
import { useQuoteTemplateDrawerModel } from './quote-template-drawer-model'
import type { QuoteTemplateDrawerProps, TemplateDetailValue } from './quote-template-drawer-model'

export type { QuoteTemplateDrawerProps } from './quote-template-drawer-model'

const actionClass = 'inline-flex h-[38px] items-center gap-1.5 rounded-[10px] border border-k-line-strong bg-transparent px-3 text-[13px] font-bold text-k-ink no-underline hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40'

const outlineBtnSx = {
    width: 'auto',
    minWidth: 'auto',
    flex: 'none',
    height: 40,
    padding: '0 16px',
    borderRadius: '12px',
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1,
    backgroundColor: 'rgb(var(--k-surface))',
    color: 'rgb(var(--k-ink))',
    border: '1px solid rgb(var(--k-line))',
    '&:hover': {
        backgroundColor: 'rgb(var(--k-hover))',
        border: '1px solid rgb(var(--k-line))',
        boxShadow: 'none',
    },
} satisfies SxProps<Theme>

const dangerBtnSx: SxProps<Theme> = {
    ...outlineBtnSx,
    backgroundColor: 'rgb(var(--k-danger))',
    color: '#fff',
    border: 0,
    '&:hover': {
        backgroundColor: 'rgb(var(--k-danger))',
        filter: 'brightness(1.08)',
        boxShadow: 'none',
    },
}

const Field = ({ label, children }: { label: string; children?: React.ReactNode }) => (
    <div>
        <dt className="mb-0.5 text-xs text-k-muted">{label}</dt>
        <dd className="m-0 break-words text-sm font-semibold text-k-ink">{children || '—'}</dd>
    </div>
)

const ValueSection = ({ title, items, empty }: { title: string; items: TemplateDetailValue[]; empty: string }) => (
    <section className="flex flex-col gap-2 rounded-2xl border border-k-line bg-k-surface px-[18px] py-4">
        <h3 className="m-0 text-[13px] font-extrabold uppercase tracking-[0.06em] text-k-muted">{title} ({items.length})</h3>
        {items.length === 0 && <p className="m-0 text-sm italic text-k-muted">{empty}</p>}
        {items.length > 0 && (
            <ul className="m-0 flex list-none flex-col p-0">
                {items.map((item) => (
                    <li key={item.key} className="flex items-baseline justify-between gap-3 border-t border-k-line py-2 text-sm first:border-t-0">
                        <span className="min-w-0 text-k-ink-2">{item.descripcion}</span>
                        <span className="shrink-0 text-right font-semibold text-k-ink">{item.valor}</span>
                    </li>
                ))}
            </ul>
        )}
    </section>
)

const QuoteTemplateDrawer = (props: QuoteTemplateDrawerProps) => {
    const { templateId, onClose, onEdit } = props
    const model = useQuoteTemplateDrawerModel(props)
    const { template, loading } = model

    return (
        <>
            <Drawer open={Boolean(templateId)} onClose={onClose} bare width={640}>
                <div className="flex h-full min-h-0 flex-col" role="dialog" aria-modal="true" aria-label={template?.nombre ? `Detalle del template ${template.nombre}` : 'Detalle del template'}>
                    <div className="flex shrink-0 flex-col gap-4 border-b border-k-line px-6 pt-5 pb-4">
                        <div className="flex items-start gap-3.5">
                            <RamoMark ramo={template?.ramo} boxed large />
                            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                <h2 className="m-0 truncate text-[21px] font-extrabold tracking-[-0.02em] text-k-ink">{template?.nombre || 'Detalle del template'}</h2>
                                {template && !loading && (
                                    <>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-k-muted">
                                            <span>{model.aseguradora}</span>
                                            {template.plan && <span>{template.plan}</span>}
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {template.ramo && <span className="rounded-full bg-k-accent-soft px-[9px] py-[3px] text-xs font-bold text-k-accent-ink">{template.ramo}</span>}
                                            <span className="rounded-full bg-k-line px-[9px] py-[3px] text-xs font-bold text-k-ink-2">{model.moneda}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                            <button type="button" aria-label="Cerrar" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-k-line bg-transparent text-k-ink hover:bg-k-hover" onClick={onClose}>
                                <CloseIcon style={{ fontSize: 18 }} />
                            </button>
                        </div>

                        {template && !loading && (
                            <div className="flex flex-wrap gap-2">
                                <GrantContent permisos={[Permisos.COTIZACIONES_TEMP_W]}>
                                    <button type="button" className={actionClass} onClick={() => onEdit?.(template.id)}>
                                        <EditOutlinedIcon style={{ fontSize: 15 }} />Editar
                                    </button>
                                </GrantContent>
                                <GrantContent permisos={[Permisos.COTIZACIONES_TEMP_D]}>
                                    <button type="button" className={`${actionClass} text-k-danger-ink hover:bg-k-danger-soft`} onClick={model.askDelete} disabled={model.deleting}>
                                        <DeleteOutlinedIcon style={{ fontSize: 15 }} />Eliminar
                                    </button>
                                </GrantContent>
                            </div>
                        )}
                    </div>

                    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-k-bg px-6 py-5">
                        {loading && (
                            <div className="flex flex-col items-center justify-center gap-4 py-12">
                                <RefreshIcon className="animate-spin text-k-primary" style={{ fontSize: 40 }} />
                                <p className="text-sm text-k-muted">Cargando detalle...</p>
                            </div>
                        )}

                        {model.error && !loading && (
                            <div className="rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                                <div className="flex items-center gap-2">
                                    <ErrorOutlineIcon className="text-k-danger" style={{ fontSize: 20 }} />
                                    <p className="text-sm text-k-danger-ink">{model.error}</p>
                                </div>
                                <Button className="mt-3" variant="contained" onClick={model.reload}>Reintentar</Button>
                            </div>
                        )}

                        {template && !loading && (
                            <>
                                <section className="flex flex-col gap-3 rounded-2xl border border-k-line bg-k-surface px-[18px] py-4">
                                    <h3 className="m-0 text-[13px] font-extrabold uppercase tracking-[0.06em] text-k-muted">Características del plan</h3>
                                    <dl className="m-0 grid grid-cols-1 gap-x-[18px] gap-y-3 sm:grid-cols-2">
                                        <Field label="Aseguradora">{model.aseguradora}</Field>
                                        <Field label="Plan o paquete">{template.plan}</Field>
                                        {model.campos.map((campo) => <Field key={campo.label} label={campo.label}>{campo.value}</Field>)}
                                    </dl>
                                </section>
                                {model.showCaracteristicas && <ValueSection title="Características adicionales" items={model.caracteristicas} empty="Sin características adicionales." />}
                                <ValueSection title="Coberturas" items={model.coberturas} empty="Sin coberturas." />
                            </>
                        )}
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2.5 border-t border-k-line bg-k-surface px-6 py-3.5">
                        <button type="button" className="h-11 rounded-xl border border-k-line-strong bg-transparent px-4 text-sm font-bold text-k-ink" onClick={onClose}>Cerrar</button>
                    </div>
                </div>
            </Drawer>

            <Dialog open={model.confirmDelete} onClose={model.cancelDelete} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Eliminar template</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">¿Desea eliminar el template? Las cotizaciones creadas con él no se modifican.</p>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" sx={outlineBtnSx} onClick={model.cancelDelete}>Cancelar</Button>
                    <Button variant="contained" sx={dangerBtnSx} onClick={model.deleteTemplate} disabled={model.deleting}>Eliminar</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default QuoteTemplateDrawer
